import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { StorageItem } from 'app/modules/models/storageItem.type';
import { StorageItemService } from 'app/shared/services/storageItem.service';

@Component({
  selector: 'app-project-display-history',
  templateUrl: './project-display-history.component.html',
  styleUrls: ['./project-display-history.component.scss']
})
export class ProjectDisplayHistoryComponent implements OnInit, OnChanges {

  @Input() rawItems: ProjectDisplayHistoryItem[] = [];
  @Input() showPartnerName: boolean = false;
  projectGroups: ProjectGroup[] = [];
  pageSize = 3;
  currentPage = 0;

  constructor(
    private _storageService: StorageItemService,
    private _snackBar: MatSnackBar,
    private _router: Router
  ) { }

  ngOnInit(): void {
    this.projectGroups = this.transformItems(this.rawItems);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rawItems']) {
      this.projectGroups = this.transformItems(this.rawItems);
      this.currentPage = 0;
    }
  }

  transformItems(rawItems: ProjectDisplayHistoryItem[]): ProjectGroup[] {
    const grouped: Record<string, ProjectGroup> = {};

    for (const item of rawItems) {
      const key = item.projectId;

      if (!grouped[key]) {
        grouped[key] = {
          projectId: item.projectId,
          projectReference: item.projectReference,
          createdOnUtc: item.createdOnUtc,
          partnerName: item.partnerName,
          files: [],
          partnerId: item.partnerId,
          isBeforeRenovation: item.isBeforeRenovation,
          isAwaitingDovaResponse: item.isAwaitingDovaResponse,
          isProjectCompleted: item.isProjectCompleted,
          hasDovaAnalysis: item.hasDovaAnalysis,
          isProjectInPreparation: item.isProjectInPreparation
        };
      }

      grouped[key].files.push(...item.files);
    }

    return Object.values(grouped);
  }

  clickDownloadFile(file: StorageItem): void {
    this._storageService.getStorageItemByNodeId(file.storageItemParentId, file.name).subscribe({
      next: (response: any) => {
        const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
        this.downloadFile(response.content, extension, file.name);
      },
      error: _ => {
        this._snackBar.open('Une erreur serveur est survenue !', null, {
          duration: 2000,
          panelClass: 'warn-snackbar',
        });
      }
    });
  }

  downloadFile(base64File: string, extension: string, fileName: string): void {
    const byteCharacters = atob(base64File);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/' + extension });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.style.display = 'none';
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  get paginatedGroups(): ProjectGroup[] {
    const start = this.currentPage * this.pageSize;
    return this.projectGroups.slice(start, start + this.pageSize);
  }

  nextPage(): void {
    if (this.currentPage + 1 < this.totalPages) this.currentPage++;
  }

  previousPage(): void {
    if (this.currentPage > 0) this.currentPage--;
  }

  get totalPages(): number {
    return Math.ceil(this.projectGroups.length / this.pageSize) || 1;
  }

  goToProject(project: ProjectGroup, event: Event): void {
    event.preventDefault(); 

    if (project.hasDovaAnalysis && project.isProjectInPreparation) {
      this._router.navigate(['project/documents-upload-summary'], {
        queryParams: {
          projectId: project.projectId,
        },
        state: {
          projectId: project.projectId,
          referenceProjet: project.projectReference,
          projectReference: project.projectReference,
          isBeforeRenovation: !!project.isBeforeRenovation,
        },
      });
      return;
    }

    // Default behaviors 
    if ((project.isBeforeRenovation && !project.isAwaitingDovaResponse) && !project.isProjectCompleted) {
      window.open('/fiche-projet-avant-travaux/' + project.projectId, '_blank');
    } else {
      window.open('/fiche-projet/' + project.projectId, '_blank');
    }
  }
}

interface ProjectDisplayHistoryItem {
  projectId: string;
  projectReference: string;
  createdOnUtc: Date | string;
  files: StorageItem[];
  partnerName?: string;
  partnerId?: string;
  isBeforeRenovation?: boolean;
  isAwaitingDovaResponse?: boolean;
  isProjectCompleted?: boolean;
  hasDovaAnalysis?: boolean;
  isProjectInPreparation?: boolean;
}

interface ProjectGroup {
  projectId: string;
  projectReference: string;
  createdOnUtc: Date | string;
  partnerName?: string;
  files: StorageItem[];
  partnerId: string;
  isBeforeRenovation?: boolean;
  isAwaitingDovaResponse?: boolean;
  isProjectCompleted?: boolean;
  hasDovaAnalysis?: boolean;
  isProjectInPreparation?: boolean;
}